import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, X, Check, Sparkles, Droplets, Zap } from "lucide-react";
import Seo from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import PricingCards from "../components/site/PricingCards";
import AddonCard from "../components/site/AddonCard";
import ReviewsSection from "../components/site/ReviewsSection";
import { SITE, DIFFERENCE, HOW_IT_WORKS, TRANSPARENCY } from "../data/site";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "CleanMyCar",
  description: "Premium doorstep car wash service in Ghaziabad. Safe, scratch free, professional cleaning.",
  telephone: SITE.phoneDisplay,
  priceRange: "₹₹",
  address: { "@type": "PostalAddress", addressLocality: "Ghaziabad", addressRegion: "UP", addressCountry: "IN" },
  areaServed: "Ghaziabad",
  openingHours: "Mo-Su 08:00-20:00",
};

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const id = location.state?.scrollTo;
    if (!id) return;
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        clearInterval(t);
      }
      if (++tries > 25) clearInterval(t);
    }, 100);
    return () => clearInterval(t);
  }, [location.state]);

  return (
    <>
      <Seo
        title="CleanMyCar — Premium Doorstep Car Wash in Ghaziabad"
        description="Stop damaging your car with daily dirty cloth wiping. Premium foam car wash at your doorstep, safe, scratch free, professional cleaning from ₹599."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section id="home" className="relative overflow-hidden pt-36 pb-24 px-6">
        <div className="glow" style={{ background: "#60B1FF", width: 460, height: 460, top: -120, left: -120 }} />
        <div className="glow" style={{ background: "#319AFF", width: 380, height: 380, top: 120, right: -80, opacity: 0.35 }} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-blue/15 bg-brand-blue/5 px-4 py-2"
              data-testid="hero-badge"
            >
              <Sparkles className="w-4 h-4 text-brand-blue" />
              <span className="text-sm font-semibold text-brand-blue">Premium Car Wash Service</span>
            </motion.div>

            <h1 className="mt-6 font-display font-extrabold tracking-tight text-brand-ink text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05]">
              <span className="line-mask">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
                  Stop Damaging Your
                </motion.span>
              </span>
              <span className="line-mask">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}>
                  Car with{" "}
                </motion.span>
              </span>
              <span className="line-mask">
                <motion.span className="block text-brand-blue italic" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}>
                  Daily Dirty Cloth Wiping
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.7 }}
              className="mt-6 text-lg text-brand-muted leading-relaxed max-w-md"
            >
              Premium Car Wash at Your Doorstep. Safe, Scratch Free, Professional Cleaning.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-book"
                onClick={() => navigate("/book")}
                className="group inline-flex items-center gap-2 bg-brand-blue text-white text-base font-semibold pl-7 pr-5 py-4 rounded-full hover:scale-[1.03] active:scale-95 transition-transform shadow-lg shadow-brand-blue/30"
              >
                Book Your Slot
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </motion.div>
          </div>

          {/* Floating circular badge with launch offer */}
          <div className="relative h-[380px] sm:h-[500px] flex items-center justify-center">
            <motion.div
              className="relative w-[300px] h-[300px] sm:w-[420px] sm:h-[420px] rounded-full hero-orb animate-float flex items-center justify-center text-center p-10"
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-offer-badge"
            >
              <div>
                <div className="font-display font-extrabold text-white text-4xl sm:text-5xl leading-none">
                  ₹200 / ₹300 OFF
                </div>
                <div className="mt-4 mx-auto w-14 h-[3px] rounded-full bg-white/70" />
                <div className="mt-4 font-display font-semibold text-white text-lg sm:text-xl leading-snug">
                  For the Initial 3 Months
                </div>
                <div className="mt-2 text-white/85 text-xs sm:text-sm tracking-[0.18em] uppercase">
                  First 1001 Customers
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-20 px-6 scroll-mt-24 overflow-hidden">
        <div className="glow" style={{ background: "#60B1FF", width: 400, height: 400, top: 40, left: "50%", transform: "translateX(-50%)", opacity: 0.25 }} />
        <div className="relative max-w-6xl mx-auto text-center mb-14">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold">Pricing</p>
            <h2 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-brand-ink">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-brand-muted max-w-xl mx-auto">
              No hidden costs. No surprises. Just premium service at a clear price.
            </p>
          </Reveal>
        </div>
        <PricingCards />
        <div className="mt-10">
          <AddonCard />
        </div>
      </section>

      {/* THE DIFFERENCE / WHY CHOOSE US */}
      <section id="difference" className="relative py-24 px-6 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold">The Difference</p>
            <h2 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-brand-ink max-w-3xl">
              Why Most Car Washes Actually Damage Your Car
            </h2>
            <p className="mt-4 text-lg text-brand-muted max-w-2xl">
              Not all car care is good car care. See what really happens vs. what we do differently.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div data-testid="difference-most" className="h-full rounded-3xl border border-black/5 bg-black/[0.02] p-8">
                <h3 className="font-display font-bold text-xl text-brand-ink">What Most Services Do</h3>
                <ul className="mt-6 space-y-4">
                  {DIFFERENCE.most.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-brand-muted">
                      <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-red-500/10 shrink-0">
                        <X className="w-3.5 h-3.5 text-red-500" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="difference-us" className="h-full rounded-3xl border border-brand-blue/20 bg-brand-blue/[0.03] p-8">
                <h3 className="font-display font-bold text-xl text-brand-blue">What CleanMyCar Does</h3>
                <ul className="mt-6 space-y-4">
                  {DIFFERENCE.us.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-brand-ink">
                      <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-brand-blue/10 shrink-0">
                        <Check className="w-3.5 h-3.5 text-brand-blue" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative py-20 px-6 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold">How It Works</p>
            <h2 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-brand-ink">
              Four simple steps. Zero hassle.
            </h2>
            <p className="mt-4 text-lg text-brand-muted">Four simple steps. Zero hassle. Complete satisfaction.</p>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-4">
            {HOW_IT_WORKS.map((item, i) => (
              <Reveal key={item.n} delay={i * 0.1}>
                <div data-testid={`how-step-${item.n}`}>
                  <div className="font-display font-extrabold text-6xl text-brand-blue/15">{item.n}</div>
                  <div className="mt-4 h-[3px] w-14 bg-brand-orange rounded-full" />
                  <h3 className="mt-5 font-display font-bold text-xl text-brand-ink">{item.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      <ReviewsSection />

      {/* SERVICE TRANSPARENCY */}
      <section id="transparency" className="py-16 px-6 scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold">Transparency</p>
            <h2 className="mt-3 font-display font-extrabold text-4xl tracking-tight text-brand-ink">
              Service Transparency
            </h2>
            <p className="mt-4 text-lg text-brand-muted">{TRANSPARENCY.intro}</p>
          </Reveal>
          <div className="mt-10 space-y-4">
            {TRANSPARENCY.points.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`transparency-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-white card-soft p-6">
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-brand-blue/10 text-brand-blue shrink-0">
                    {i === 0 ? <Droplets className="w-5 h-5" /> : i === 1 ? <Check className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                  </span>
                  <p className="text-brand-muted leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GET STARTED CTA */}
      <section id="contact" className="px-6 scroll-mt-24">
        <div className="max-w-6xl mx-auto relative overflow-hidden rounded-[2rem] bg-brand-blue text-white px-8 py-16 text-center">
          <div className="glow" style={{ background: "#60B1FF", width: 340, height: 340, top: -100, right: -60, opacity: 0.5 }} />
          <div className="relative">
            <p className="text-sm uppercase tracking-[0.25em] text-white/70 font-semibold">Get Started Today</p>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-5xl tracking-tight">
              Your Car Deserves Better Than <span className="italic">Wiping</span>
            </h2>
            <p className="mt-4 text-white/80 max-w-lg mx-auto">
              Try once and experience the difference. No commitment required for your first wash.
            </p>
            <button
              data-testid="cta-strip-book"
              onClick={() => navigate("/book")}
              className="mt-7 inline-flex items-center gap-2 bg-white text-brand-blue font-semibold px-7 py-4 rounded-full hover:scale-[1.03] transition-transform"
            >
              Book Your Slot <ArrowUpRight className="w-5 h-5" />
            </button>
            <p className="mt-5 text-sm text-white/70">Limited slots available, book yours today</p>
          </div>
        </div>
      </section>
    </>
  );
}
